import React, { useEffect, useState } from "react"
import expandIcon from "../../../static/assets/image/expand-icon.svg"
import collapseIcon from "../../../static/assets/image/collapse-icon.svg"
import rightArrow from "../../../static/assets/image/home/right-arrow.svg"

const styles = {
  normalFrame: {
    background: "#fff",
    border: "1px solid #d9dde3",
    borderRadius: "12px",
    minHeight: "820px",
    overflow: "hidden",
    position: "relative",
  },
  expandedFrame: {
    background: "#fff",
    border: "1px solid #d9dde3",
    borderRadius: "12px",
    bottom: "25px",
    boxShadow: "0 24px 72px rgba(15, 23, 42, 0.24)",
    left: "25px",
    overflow: "hidden",
    position: "fixed",
    right: "25px",
    top: "75px",
    zIndex: 1000,
  },
  normalEmbed: {
    display: "block",
    width: "100%",
    minHeight: "820px",
  },
  expandedEmbed: {
    display: "block",
    height: "100%",
    width: "100%",
  },
  backdrop: {
    background: "rgba(15, 23, 42, 0.28)",
    inset: 0,
    opacity: 0,
    pointerEvents: "none",
    position: "fixed",
    transition: "opacity 50ms ease",
    zIndex: 999,
  },
  visibleBackdrop: {
    backdropFilter: "blur(8px)",
    opacity: 1,
    pointerEvents: "auto",
    WebkitBackdropFilter: "blur(8px)",
  },
  message: {
    background: "#fff7f7",
    border: "1px solid #f0caca",
    borderRadius: "12px",
    color: "#8a1f1f",
    padding: "16px 18px",
  },
  loading: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d9dde3",
    borderRadius: "12px",
    color: "#505050",
    display: "flex",
    justifyContent: "center",
    minHeight: "320px",
    padding: "24px",
    textAlign: "center",
  },
  actions: {
    marginTop: "12px",
  },
  normalActions: {
    opacity: 1,
    marginBottom: "12px",
    transform: "translateY(-4px)",
    transition: "opacity 50ms ease, transform 50ms ease",
  },
  expandedActions: {
    opacity: 1,
    position: "fixed",
    right: "25px",
    top: "0",
    transform: "translateY(0)",
    transition: "opacity 50ms ease, transform 50ms ease",
    zIndex: 1001,
  },
  button: {
    background: "#bf1116",
    border: 0,
    borderRadius: "999px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 16px",
  },
}

const getQlikEmbedLanguage = pageLocale =>
  String(pageLocale || "")
    .toLowerCase()
    .startsWith("fr")
    ? "fr-FR"
    : "en-US"

const QlikClassicAppEmbed = ({
  tenantUrl,
  clientId,
  appId,
  sheetId,
  pageLocale = "en-CA",
  loadingText = "Loading Media Insights Platform…",
  heading,
  sourceText,
  sourceCta = null,
}) => {
  const [scriptState, setScriptState] = useState("loading")
  const [errorMessage, setErrorMessage] = useState("")
  const [isExpanded, setIsExpanded] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const sourceCtaLabel = String(sourceCta?.label || "").trim()
  const sourceCtaUrl = String(sourceCta?.url || "").trim()
  const hasSourceCta = Boolean(sourceCtaLabel && sourceCtaUrl)
  const qlikLanguage = getQlikEmbedLanguage(pageLocale)

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    let isMounted = true
    let pollId = null
    let timeoutId = null

    const setMountedState = (nextState, nextError = "") => {
      if (!isMounted) {
        return
      }

      setScriptState(nextState)
      setErrorMessage(nextError)
    }

    const resolveErrorMessage = payload => {
      if (payload?.stage) {
        return `Qlik authentication failed during ${payload.stage.replace(
          "_",
          " "
        )}.`
      }

      return "Unable to get a Qlik access token right now."
    }

    window.getAccessToken = async () => {
      const response = await fetch("/.netlify/functions/qlik-access-token", {
        method: "GET",
        credentials: "include",
        mode: "same-origin",
        cache: "no-store",
      })

      let payload = null
      const text = await response.text()

      if (text) {
        try {
          payload = JSON.parse(text)
        } catch (error) {
          payload = { detail: text }
        }
      }

      if (!response.ok) {
        setMountedState("error", resolveErrorMessage(payload))

        const error = new Error(
          payload?.error || "Unable to get Qlik access token"
        )
        error.status = response.status
        error.stage = payload?.stage
        error.detail = payload?.detail || text
        throw error
      }

      if (!payload?.accessToken) {
        setMountedState(
          "error",
          "Qlik authentication succeeded, but no access token was returned."
        )
        throw new Error(
          "Qlik access token response did not include accessToken"
        )
      }

      return payload.accessToken
    }

    const checkReady = () => {
      if (typeof window.customElements === "undefined") {
        return
      }

      if (window.customElements.get("qlik-embed")) {
        if (pollId) {
          window.clearInterval(pollId)
        }
        if (timeoutId) {
          window.clearTimeout(timeoutId)
        }
        setMountedState("ready")
      }
    }

    checkReady()

    if (!window.customElements?.get("qlik-embed")) {
      pollId = window.setInterval(checkReady, 250)
      timeoutId = window.setTimeout(() => {
        if (pollId) {
          window.clearInterval(pollId)
        }
        setMountedState(
          "error",
          "The Qlik embed script did not become ready. Please refresh and try again."
        )
      }, 12000)
    }

    return () => {
      isMounted = false
      if (pollId) {
        window.clearInterval(pollId)
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      if (window.getAccessToken) {
        delete window.getAccessToken
      }
    }
  }, [retryKey])

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    let resizeTimeoutId = null
    let resizeFrameId = null

    document.body.classList.toggle("qlik-takeover-active", isExpanded)
    document.body.style.overflow = isExpanded ? "hidden" : previousOverflow

    resizeFrameId = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"))
    })

    resizeTimeoutId = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"))
    }, 50)

    return () => {
      if (resizeFrameId) {
        window.cancelAnimationFrame(resizeFrameId)
      }
      if (resizeTimeoutId) {
        window.clearTimeout(resizeTimeoutId)
      }
      document.body.style.overflow = previousOverflow
      document.body.classList.remove("qlik-takeover-active")
    }
  }, [isExpanded])

  if (!tenantUrl || !clientId || !appId || !sheetId) {
    return <div style={styles.message}>Qlik embed is not configured.</div>
  }

  if (scriptState === "loading") {
    return <div style={styles.loading}>{loadingText}</div>
  }

  if (scriptState === "error") {
    return (
      <div style={styles.message}>
        <div>{errorMessage || "Unable to load Media Insights Platform…"}</div>
        <div style={styles.actions}>
          <button
            type="button"
            style={styles.button}
            onClick={() => {
              setErrorMessage("")
              setScriptState("loading")
              setRetryKey(current => current + 1)
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return React.createElement(
    "div",
    null,
    React.createElement("div", {
      style: {
        ...styles.backdrop,
        ...(isExpanded ? styles.visibleBackdrop : null),
      },
      "aria-hidden": "true",
      onClick: () => {
        if (isExpanded) {
          setIsExpanded(false)
        }
      },
    }),
    React.createElement(
      "div",
      {
        className: "qlik_embed__actions",
        style: isExpanded ? styles.expandedActions : styles.normalActions,
      },
      React.createElement(
        "h2",
        { className: "qlik_embed__heading qlik_embed__heading--above" },
        heading
      ),
      React.createElement(
        "button",
        {
          type: "button",
          className: "new_cta new_cta--transparent",
          onClick: () => {
            setIsExpanded(current => !current)
          },
        },
        React.createElement(
          "span",
          { className: "new_cta_text" },
          isExpanded ? "Collapse" : "Expand"
        ),
        React.createElement("img", {
          src: isExpanded ? collapseIcon : expandIcon,
          alt: "",
          "aria-hidden": "true",
          className: "new_cta_icon",
        })
      )
    ),
    React.createElement(
      "div",
      {
        style: isExpanded ? styles.expandedFrame : styles.normalFrame,
        className: "qlik-embed-wrapper-new",
      },
      React.createElement("qlik-embed", {
        key: `${appId}:${sheetId}:${qlikLanguage}`,
        ui: "classic/app",
        "app-id": appId,
        "sheet-id": sheetId,
        language: qlikLanguage,
        "data-testid": "qlik-classic-app",
        style: isExpanded ? styles.expandedEmbed : styles.normalEmbed,
      })
    ),
    React.createElement(
      "div",
      { className: "qlik_embed__source" },
      React.createElement(
        "h2",
        { className: "qlik_embed__heading qlik_embed__heading--below" },
        sourceText
      ),
      hasSourceCta
        ? React.createElement(
            "a",
            {
              className: "new_cta",
              href: sourceCtaUrl,
              target: "_blank",
              rel: "noopener noreferrer",
            },
            React.createElement(
              "span",
              { className: "new_cta_text" },
              sourceCtaLabel
            ),
            React.createElement(
              "span",
              { className: "new_cta_arrow", "aria-hidden": "true" },
              React.createElement("img", { src: rightArrow, alt: "" })
            )
          )
        : null
    )
  )
}

export default QlikClassicAppEmbed
